<template>
  <div v-if="open" class="command-palette__overlay" @mousedown.self="close">
    <div class="command-palette" role="dialog" aria-label="Pesquisar módulos">
      <div class="command-palette__search">
        <fa-icon icon="search" class="command-palette__icon" />
        <input
          ref="input"
          v-model="filter"
          type="text"
          placeholder="Pesquisar módulo..."
          class="command-palette__input"
          @keydown.down.prevent="moveSelection(1)"
          @keydown.up.prevent="moveSelection(-1)"
          @keydown.enter.prevent="selectActive"
          @keydown.esc.prevent="close"
          @keydown.tab.prevent="moveSelection($event.shiftKey ? -1 : 1)"
        />
        <span class="command-palette__hint">esc</span>
      </div>

      <div class="command-palette__results" ref="results">
        <template v-if="results.length">
          <div
            v-for="(item, index) in results"
            :key="item.href"
            :class="[
              'command-palette__item',
              { 'command-palette__item--active': index === activeIndex },
            ]"
            @mousemove="activeIndex = index"
            @mousedown.prevent="selectItem(item)"
          >
            <CIcon :name="item.icon" class="command-palette__item-icon" />
            <div class="command-palette__item-body">
              <span class="command-palette__item-name">{{ item.name }}</span>
              <span class="command-palette__item-group">{{ item.group }}</span>
            </div>
          </div>
        </template>
        <div v-else class="command-palette__empty">
          Nenhum módulo encontrado para "{{ filter }}"
        </div>
      </div>

      <div class="command-palette__footer">
        <span><kbd>↑</kbd><kbd>↓</kbd> navegar</span>
        <span><kbd>↵</kbd> selecionar</span>
        <span><kbd>esc</kbd> fechar</span>
      </div>
    </div>
  </div>
</template>

<script>
import sidebarMenus from "../../config/sideBar";
import { stringContainsObject } from "@/helpers/utils";
import _find from "lodash/find";

export default {
  name: "CommandPalette",
  props: {
    modulos: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      open: false,
      filter: "",
      activeIndex: 0,
    };
  },
  computed: {
    allItems() {
      const items = [];
      sidebarMenus.forEach((group) => {
        (group.children || []).forEach((child) => {
          const modulo = _find(this.modulos, { nome: child.modulo });
          if (modulo && modulo.autorizado) {
            items.push({
              href: child.href,
              icon: child.icon,
              modulo: child.modulo,
              name: modulo.descPlural || modulo.descSingular || child.modulo,
              group: group.menu,
              descSingular: modulo.descSingular,
            });
          }
        });
      });
      return items;
    },
    results() {
      if (!this.filter) {
        return this.allItems;
      }
      return this.allItems.filter((item) =>
        stringContainsObject(
          this.filter,
          ["name", "group", "href", "modulo", "descSingular"],
          item
        )
      );
    },
  },
  watch: {
    filter() {
      this.activeIndex = 0;
    },
    activeIndex() {
      this.$nextTick(this.scrollActiveIntoView);
    },
    open(value) {
      if (value) {
        this.$nextTick(() => {
          if (this.$refs.input) this.$refs.input.focus();
        });
      }
    },
  },
  mounted() {
    document.addEventListener("keydown", this.onGlobalKeyDown);
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this.onGlobalKeyDown);
  },
  methods: {
    onGlobalKeyDown(event) {
      if (event.ctrlKey && event.code === "Space") {
        event.preventDefault();
        this.toggle();
      }
    },
    toggle() {
      this.open ? this.close() : this.show();
    },
    show() {
      this.filter = "";
      this.activeIndex = 0;
      this.open = true;
    },
    close() {
      this.open = false;
    },
    moveSelection(delta) {
      if (!this.results.length) return;
      const total = this.results.length;
      this.activeIndex = (this.activeIndex + delta + total) % total;
    },
    selectActive() {
      const item = this.results[this.activeIndex];
      if (item) this.selectItem(item);
    },
    selectItem(item) {
      this.close();
      if (this.$route.path !== item.href) {
        this.$router.push(item.href);
      }
    },
    scrollActiveIntoView() {
      const container = this.$refs.results;
      if (!container) return;
      const active = container.querySelector(".command-palette__item--active");
      if (active && active.scrollIntoView) {
        active.scrollIntoView({ block: "nearest" });
      }
    },
  },
};
</script>

<style scoped>
.command-palette__overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 12vh;
  z-index: 9999;
}

.command-palette {
  width: min(560px, 92vw);
  max-height: 70vh;
  background: #fff;
  border: 1px solid rgba(159, 167, 179, 0.5);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  color: rgba(0, 0, 21, 0.8);
}

.command-palette__search {
  display: flex;
  align-items: center;
  padding: 0.8445rem 1rem;
  border-bottom: 1px solid rgba(159, 167, 179, 0.5);
}

.command-palette__icon {
  flex: 0 0 56px;
  height: 1.09375rem;
  font-size: 1.09375rem;
  text-align: center;
  margin-left: -1rem;
  color: rgba(0, 0, 21, 0.5);
}

.command-palette__input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 0.875rem;
  background: transparent;
  color: rgba(0, 0, 21, 0.8);
  font-family: inherit;
}

.command-palette__input::placeholder {
  color: rgba(0, 0, 21, 0.4);
}

.command-palette__hint {
  font-size: 11px;
  color: rgba(0, 0, 21, 0.5);
  background: rgba(0, 0, 21, 0.05);
  border-radius: 3px;
  padding: 2px 6px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.command-palette__results {
  flex: 1;
  overflow-y: auto;
}

.command-palette__item {
  display: flex;
  align-items: center;
  padding: 0.8445rem 1rem;
  cursor: pointer;
  color: rgba(0, 0, 21, 0.8);
  background: transparent;
  text-decoration: none;
  white-space: nowrap;
  transition: background 0.3s, color 0.3s;
}

.command-palette__item:hover,
.command-palette__item--active {
  color: #fff;
  background: #004576;
}

.command-palette__item-icon {
  flex: 0 0 56px;
  height: 1.09375rem;
  font-size: 1.09375rem;
  text-align: center;
  margin-left: -1rem;
  color: rgba(0, 0, 21, 0.5);
  transition: 0.3s;
  fill: currentColor;
}

.command-palette__item:hover .command-palette__item-icon,
.command-palette__item--active .command-palette__item-icon {
  color: #fff;
}

.command-palette__item-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
}

.command-palette__item-name {
  font-size: 0.875rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.command-palette__item-group {
  font-size: 11px;
  opacity: 0.65;
  margin-top: 2px;
}

.command-palette__empty {
  padding: 28px 18px;
  text-align: center;
  color: rgba(0, 0, 21, 0.4);
  font-size: 0.875rem;
}

.command-palette__footer {
  display: flex;
  gap: 18px;
  padding: 0.625rem 1rem;
  border-top: 1px solid rgba(159, 167, 179, 0.5);
  background: rgba(0, 0, 21, 0.03);
  font-size: 11px;
  color: rgba(0, 0, 21, 0.5);
}

.command-palette__footer kbd {
  background: #fff;
  border: 1px solid rgba(159, 167, 179, 0.5);
  border-bottom-width: 2px;
  border-radius: 3px;
  padding: 1px 5px;
  margin-right: 4px;
  font-family: inherit;
  font-size: 11px;
  color: rgba(0, 0, 21, 0.7);
}
</style>
